import React, { useId, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";
import { API_BASE_URL } from "../../constants";
import PrimaryButton from "../../UI/PrimaryButton";

const RegisterForm = ({ toggleForm }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const nameId = useId();
  const emailId = useId();
  const passwordId = useId();
  const confirmPasswordId = useId();

  const { t } = useTranslation();

  const handleRegister = async (event) => {
    event.preventDefault();
    setLoading(true);

    if (password.length < 8) {
      enqueueSnackbar("Password must be at least 8 characters long.", {
        variant: "error",
      });
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      enqueueSnackbar("Passwords do not match", { variant: "error" });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Registration failed:", errorData);
        return;
      }

      const data = await response.json();
      enqueueSnackbar(data.message, { variant: "success" });

      setTimeout(() => {
        toggleForm("login");
      }, 100);
    } catch (error) {
      console.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-10 z-50"
      onClick={() => toggleForm(null)}
    >
      <div
        className="bg-white p-8 rounded-lg shadow-2xl max-w-md w-full relative m-2"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => toggleForm(null)}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 focus:outline-none"
          aria-label="Close register form"
        >
          ✕
        </button>
        <h1 className="text-gray-800 text-2xl font-bold text-center mb-6">
          {t("signUp")}
        </h1>
        <form onSubmit={handleRegister} className="space-y-6">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-600 mb-2"
            >
              {t("name")}
            </label>
            <input
              type="text"
              id={nameId}
              placeholder={t("enterYourName")}
              className="w-full p-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-baseGreen"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-600 mb-2"
            >
              {t("email")}
            </label>
            <input
              type="email"
              id={emailId}
              placeholder={t("enterYourEmail")}
              className="w-full p-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-baseGreen"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="relative">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-600 mb-2"
            >
              {t("password")}
            </label>
            <input
              type={showPassword ? "text" : "password"}
              id={passwordId}
              placeholder={t("enterYourPassword")}
              className="w-full p-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-baseGreen"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute inset-y-0 right-0 top-6 flex items-center pr-3 text-gray-400 hover:text-baseGreen focus:outline-none"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          <div className="relative">
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-600 mb-2"
            >
              {t("confirmPassword")}
            </label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              id={confirmPasswordId}
              placeholder={t("confirmYourPassword")}
              className="w-full p-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-baseGreen"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={toggleConfirmPasswordVisibility}
              className="absolute inset-y-0 right-0 top-6 flex items-center pr-3 text-gray-400 hover:text-baseGreen focus:outline-none"
            >
              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          <div>
          <div>
            <PrimaryButton
              text={loading ? <LoadingSpinner /> : t("signUp")}
              type="submit"
              className={
                loading
                  ? "opacity-75 cursor-not-allowed flex justify-center items-center"
                  : ""
              }
              disabled={loading}
            />
          </div>
          </div>
        </form>
        <p className="text-gray-500 text-center mt-4">
          {t("alreadyHaveAccount")}{" "}
          <span
            onClick={() => toggleForm("login")}
            className="text-baseBlue underline cursor-pointer"
          >
            {t("login")}
          </span>
        </p>
      </div>
    </div>
  );
};

export default RegisterForm;
